import Show from '@/components/Show';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import JenErrorToast from '@/components/ui/jen-error-toast';
import { useDragAndDrop } from '@/hooks/useDragAndDrop';
import useGeneratorStore, { R3ImageTypeEnum, R3imagineStatusType, RecordingStatusEnum } from '@/state/generator-store';
import { cn } from '@/utils';
import { displayDuration } from '@/utils/time';
import useFlags from '@jenmusic/feature-flag';
import React from 'react';
import { toast } from 'sonner';
import { useModel } from '../model';
import FileSelect from './FileSelect';
import RecordingConfirm from './RecordingConfirm';
import { UploadArea } from './UploadArea';
import WarningModal from './WarningModal';

interface FileUploadCardProps {
  disabled?: boolean;
  hide?: () => void;
}

const FileUploadCard: React.FC<FileUploadCardProps> = ({ disabled = false, hide }) => {
  const { fileInputRef, handleUpload, handleClickUpload } = useModel();

  const { r3ImageStatus, setR3ImageStatus, isCountdown, recordingTime, recordingStatus, setR3ImageType } = useGeneratorStore();

  const { showNewGenerator } = useFlags();

  const isDisabled = disabled || isCountdown;

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isDisabled) return;
    setR3ImageStatus(R3imagineStatusType.UploadLoading);
    try {
      await handleUpload(event);
      setR3ImageType(R3ImageTypeEnum.upload);
    } catch (error) {
      setR3ImageStatus(R3imagineStatusType.WaitingUpload);
      toast(<JenErrorToast error={new Error('Upload failed, please try again.')} />, {
        className: '!border-0 !shadow-none',
        unstyled: true,
      });
    } finally {
      setR3ImageStatus(R3imagineStatusType.WaitingUpload);
    }
  };

  const { isDragging, dragProps } = useDragAndDrop({
    isDisabled,
    onFilesDrop: (files) => {
      const event = {
        target: { files },
      } as React.ChangeEvent<HTMLInputElement>;
      handleFileUpload(event);
    },
  });

  return (
    <div className="bg-[#383A3C] rounded-md p-1">
      <div className="bg-[#191A1B] rounded-md">
        <Card className={`bg-[#191A1B] w-full border-none ${isDisabled ? 'pointer-events-none' : ''}`}>
          <Show when={recordingStatus === RecordingStatusEnum.WaitingRecording}>
            <>
              <CardHeader className="p-4">
                <CardTitle className="text-jen-orange text-[10px] font-pressura-mono font-normal uppercase">
                  UPLOAD OR CREATE <br /> FROM AN EXISTING TRACK:
                </CardTitle>
              </CardHeader>
              <CardContent
                className={cn(
                  'flex flex-col items-center space-y-3 h-48 w-full relative',
                  showNewGenerator ? 'py-0 px-4' : 'p-4',
                  isDragging && 'border-3 border-dashed border-jen-orange bg-opacity-10 bg-jen-orange',
                )}
                {...dragProps}
              >
                <UploadArea
                  isDragging={isDragging}
                  showNewGenerator={showNewGenerator}
                  fileInputRef={fileInputRef}
                  onFileUpload={handleFileUpload}
                  onClickUpload={handleClickUpload}
                  r3ImageStatus={r3ImageStatus}
                />
              </CardContent>
              <FileSelect />
            </>
          </Show>
          <Show when={recordingStatus === RecordingStatusEnum.Recording}>
            <>
              <CardHeader className="p-4">
                <CardTitle className="text-jen-orange text-[10px] font-pressura-mono font-weight-[400]">
                  RECORDING <br /> IN PROGRESS
                </CardTitle>
              </CardHeader>
              <CardContent
                className={cn('flex flex-col items-center justify-center space-y-3 p-4 w-full h-', showNewGenerator ? ' h-64' : ' h-48')}
              >
                <div className="text-jen-orange text-[60px] font-NB-International-Pro font-weight-[400] break-words flex items-center justify-center h-full">
                  {displayDuration(recordingTime, true)}
                </div>
              </CardContent>
            </>
          </Show>
          <Show when={recordingStatus === RecordingStatusEnum.RecordingFinished}>
            <RecordingConfirm hide={hide} />
          </Show>
          <Show when={recordingStatus === RecordingStatusEnum.RecordingWarning}>
            <WarningModal />
          </Show>
        </Card>
      </div>
    </div>
  );
};

export default FileUploadCard;
